import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getCurrentRichMenu } from 'api/richMenu'
import { queryKeys } from 'constants/queryKeys'
import { lineStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import { toErrorMessage } from 'hooks/utils'
import type { TRichMenuCurrentResponse } from 'types'

type UseCurrentRichMenuResult = {
  data: TRichMenuCurrentResponse | undefined
  imageUrl: string | null
  loading: boolean
  error: string | undefined
}

export function useCurrentRichMenu(): UseCurrentRichMenuResult {
  const { user } = useAuth()
  const [imageUrl, setImageUrl] = useState<string | null>(null)

  const query = useQuery({
    queryKey: [queryKeys.RICH_MENU_CURRENT, user?.uid],
    enabled: Boolean(user?.uid),
    queryFn: getCurrentRichMenu,
    retry: false,
  })

  useEffect(() => {
    const base64 = query.data?.imageBase64
    const contentType = query.data?.imageContentType
    if (!base64 || !contentType) {
      setImageUrl(null)
      return
    }

    let active = true
    let objectUrl: string | null = null
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
    const blob = new Blob([bytes], { type: contentType })

    objectUrl = URL.createObjectURL(blob)
    if (active) setImageUrl(objectUrl)

    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [query.data?.imageBase64, query.data?.imageContentType])

  return {
    data: query.data,
    imageUrl,
    loading: Boolean(user?.uid) && query.isPending,
    error: query.error
      ? toErrorMessage(
        query.error,
        lineStrings.management.richMenu.create.errors.loadCurrentRichMenuFailed,
      )
      : undefined,
  }
}
